import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQuery } from '@apollo/client';
import { Button, IconButton, TextInput, Typography } from '@guildeducationinc/recess';
import AddOutlinedIcon from '@guildeducationinc/recess/icons/AddOutlinedIcon';
import DeleteOutlinedIcon from '@guildeducationinc/recess/icons/DeleteOutlinedIcon';
import { TuitionCalculationLineItemType, } from '@src/types/tuitionCalculation';
import { getActiveItems, isCourseItem, isFeeItem, isBookMaterialItem, isScholarshipItem, } from '@src/utilities/paymentRequestHelper';
import { useAlert, useLocale } from 'pe-core-frontend';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { uniqueId } from 'lodash';
import { GET_TUITION_CALCULATION, SAVE_TUITION_CALCULATION, } from '@src/graphql/queries/tuitionCalculation';
import styles from './TuitionCalculator.module.scss';
/**
 * Helper function to create an empty line item of a given type
 */
const createEmptyLineItem = (type) => ({
    tempId: uniqueId(),
    type,
    title: '',
});
/**
 * Helper function to load and transform line items by type from saved calculation
 */
const loadLineItemsByType = (items, type) => {
    const filtered = items
        .filter(item => item.type === type)
        .map(item => ({
        ...item,
        amount: item.amount?.toFixed(2),
        tempId: uniqueId(),
    }));
    // Always return at least one empty row
    return filtered.length > 0 ? filtered : [createEmptyLineItem(type)];
};
/**
 * Tuition Calculator component that saves calculations to the database.
 * Matches the Grow reimbursement tuition calculator UI.
 */
export const TuitionCalculator = ({ paymentRequest }) => {
    const { t, formatDecimal, getCurrencySymbol, formatToLocalDatetime } = useLocale();
    const { showAlert } = useAlert();
    const currency = useMemo(() => paymentRequest.client.clientConfig.currency, [paymentRequest.client]);
    const currencyDisplay = getCurrencySymbol(currency, true);
    // Get active items from payment request for pre-filling
    const activeItems = useMemo(() => getActiveItems(paymentRequest), [paymentRequest]);
    // Initialize tuition costs from payment request course, fee, book, and material items
    const initTuitionCosts = useMemo(() => {
        const courses = activeItems
            .filter(item => isCourseItem(item))
            .map(item => ({
            tempId: uniqueId(),
            type: TuitionCalculationLineItemType.TUITION_COST,
            title: item.courseCode || '',
            amount: item.amountRequested?.toString() || '',
        }));
        const fees = activeItems
            .filter(item => isFeeItem(item))
            .map(item => ({
            tempId: uniqueId(),
            type: TuitionCalculationLineItemType.TUITION_COST,
            title: item.title || '',
            amount: item.amountRequested?.toString() || '',
        }));
        const booksMaterials = activeItems
            .filter(item => isBookMaterialItem(item))
            .map(item => ({
            tempId: uniqueId(),
            type: TuitionCalculationLineItemType.TUITION_COST,
            title: item.title || '',
            amount: item.amountRequested?.toString() || '',
        }));
        const items = [...courses, ...fees, ...booksMaterials];
        // Always have at least one empty row
        return items.length > 0
            ? items
            : [createEmptyLineItem(TuitionCalculationLineItemType.TUITION_COST)];
    }, [activeItems]);
    // Initialize non-reimbursable fees (voluntary meals, payment plans, convenience fees, etc.)
    // These are not pre-populated from payment request items
    const initNonReimbursableFees = useMemo(() => {
        // Always start with one empty row for manual entry
        return [createEmptyLineItem(TuitionCalculationLineItemType.NON_REIMBURSABLE_FEE)];
    }, []);
    // Initialize scholarships and grants from payment request scholarship items
    const initScholarshipsAndGrants = useMemo(() => {
        const scholarships = activeItems
            .filter(item => isScholarshipItem(item))
            .map(item => ({
            tempId: uniqueId(),
            type: TuitionCalculationLineItemType.SCHOLARSHIP_GRANT,
            title: item.title || '',
            amount: item.amountRequested?.toString() || '',
        }));
        // Always have at least one empty row
        return scholarships.length > 0
            ? scholarships
            : [createEmptyLineItem(TuitionCalculationLineItemType.SCHOLARSHIP_GRANT)];
    }, [activeItems]);
    // State for all line items grouped by type
    const [tuitionCosts, setTuitionCosts] = useState([
        createEmptyLineItem(TuitionCalculationLineItemType.TUITION_COST),
    ]);
    const [nonReimbursableFees, setNonReimbursableFees] = useState([
        createEmptyLineItem(TuitionCalculationLineItemType.NON_REIMBURSABLE_FEE),
    ]);
    const [creditsTuition, setCreditsTuition] = useState([
        createEmptyLineItem(TuitionCalculationLineItemType.CREDIT_TUITION),
    ]);
    const [scholarshipsAndGrants, setScholarshipsAndGrants] = useState([
        createEmptyLineItem(TuitionCalculationLineItemType.SCHOLARSHIP_GRANT),
    ]);
    const [roomAndBoard, setRoomAndBoard] = useState([
        createEmptyLineItem(TuitionCalculationLineItemType.ROOM_AND_BOARD),
    ]);
    const [creditsHousing, setCreditsHousing] = useState([
        createEmptyLineItem(TuitionCalculationLineItemType.CREDIT_HOUSING),
    ]);
    const [notes, setNotes] = useState('');
    const [focusTempId, setFocusTempId] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const inputRefs = useRef(new Map());
    // Load existing calculation
    const { data: calculationData, loading, refetch, } = useQuery(GET_TUITION_CALCULATION, {
        variables: { paymentRequestId: paymentRequest.id },
        skip: !paymentRequest.id,
    });
    // Save mutation
    const [saveMutation, { loading: saving }] = useMutation(SAVE_TUITION_CALCULATION);
    // Focus and select input when a new item is added
    useEffect(() => {
        if (focusTempId) {
            const input = inputRefs.current.get(focusTempId);
            if (input) {
                input.focus();
                input.select();
                setFocusTempId(null);
            }
        }
    }, [focusTempId]);
    // Load saved calculation into state, or pre-fill from payment request if no saved calculation
    useEffect(() => {
        // Only initialize once when the query completes
        if (loading || isInitialized)
            return;
        const savedCalc = calculationData?.tuitionCalculation;
        if (savedCalc && savedCalc.lineItems) {
            // Load saved calculation
            setNotes(savedCalc.notes || '');
            // Group line items by type using helper function
            const items = savedCalc.lineItems;
            setTuitionCosts(loadLineItemsByType(items, TuitionCalculationLineItemType.TUITION_COST));
            setNonReimbursableFees(loadLineItemsByType(items, TuitionCalculationLineItemType.NON_REIMBURSABLE_FEE));
            setCreditsTuition(loadLineItemsByType(items, TuitionCalculationLineItemType.CREDIT_TUITION));
            // Load scholarships and grants
            setScholarshipsAndGrants(loadLineItemsByType(items, TuitionCalculationLineItemType.SCHOLARSHIP_GRANT));
            setRoomAndBoard(loadLineItemsByType(items, TuitionCalculationLineItemType.ROOM_AND_BOARD));
            setCreditsHousing(loadLineItemsByType(items, TuitionCalculationLineItemType.CREDIT_HOUSING));
        }
        else {
            // No saved calculation - pre-fill from payment request
            setTuitionCosts(initTuitionCosts);
            setNonReimbursableFees(initNonReimbursableFees);
            setScholarshipsAndGrants(initScholarshipsAndGrants);
        }
        setIsInitialized(true);
    }, [
        calculationData,
        loading,
        isInitialized,
        initTuitionCosts,
        initNonReimbursableFees,
        initScholarshipsAndGrants,
    ]);
    // Calculate totals
    const totals = useMemo(() => {
        const sumItems = (items) => items.reduce((sum, item) => sum + (parseFloat(item.amount || '0') || 0), 0);
        const totalTuitionAndFees = sumItems(tuitionCosts);
        const totalNonReimbursableFees = sumItems(nonReimbursableFees);
        const totalCreditsTuition = sumItems(creditsTuition);
        const subtotalAfterCredits = Math.max(totalTuitionAndFees - totalCreditsTuition, 0);
        const totalGrantsScholarships = sumItems(scholarshipsAndGrants);
        const totalRoomAndBoard = sumItems(roomAndBoard);
        const totalCreditsHousing = sumItems(creditsHousing);
        const roomAndBoardAfterCredits = Math.max(totalRoomAndBoard - totalCreditsHousing, 0);
        // Grants & scholarships cover room & board first. Room & board is never reimbursable, so
        // any room & board left over after grants must not be added to the reimbursable amount.
        const grantsRemainingAfterRoomAndBoard = Math.max(totalGrantsScholarships - roomAndBoardAfterCredits, 0);
        const reimbursableTuition = Math.max(0, subtotalAfterCredits - grantsRemainingAfterRoomAndBoard);
        return {
            totalTuitionAndFees,
            totalNonReimbursableFees,
            totalCreditsTuition,
            subtotalAfterCredits,
            totalGrantsScholarships,
            totalRoomAndBoard,
            totalCreditsHousing,
            roomAndBoardAfterCredits,
            reimbursableTuition,
        };
    }, [
        tuitionCosts,
        nonReimbursableFees,
        creditsTuition,
        scholarshipsAndGrants,
        roomAndBoard,
        creditsHousing,
    ]);
    // Handlers for line items
    const handleAddItem = useCallback((setter, type) => {
        const newTempId = uniqueId();
        setter(prev => [...prev, { tempId: newTempId, type, title: '' }]);
        setFocusTempId(newTempId);
        if (isInitialized)
            setHasUnsavedChanges(true);
    }, [isInitialized]);
    const handleRemoveItem = useCallback((setter, tempId) => {
        setter(prev => prev.filter(item => item.tempId !== tempId));
        if (isInitialized)
            setHasUnsavedChanges(true);
    }, [isInitialized]);
    const handleUpdateItem = useCallback((setter, tempId, field, value) => {
        setter(prev => prev.map(item => (item.tempId === tempId ? { ...item, [field]: value } : item)));
        if (isInitialized)
            setHasUnsavedChanges(true);
    }, [isInitialized]);
    // Save handler
    const handleSave = useCallback(async () => {
        const allLineItems = [
            ...tuitionCosts,
            ...nonReimbursableFees,
            ...creditsTuition,
            ...scholarshipsAndGrants,
            ...roomAndBoard,
            ...creditsHousing,
        ]
            .filter(item => {
            // Skip rows with no amount
            if (!item.amount)
                return false;
            // Skip rows with empty or whitespace-only amounts
            const trimmedAmount = item.amount.trim();
            if (trimmedAmount === '')
                return false;
            // Skip rows with invalid numeric amounts
            const numericAmount = parseFloat(trimmedAmount);
            if (isNaN(numericAmount))
                return false;
            return true;
        })
            .map(({ tempId, amount, ...item }) => ({
            ...item,
            amount: amount ? parseFloat(amount) : undefined,
        }));
        const input = {
            id: calculationData?.tuitionCalculation?.id,
            paymentRequestId: paymentRequest.id,
            clientId: paymentRequest.clientId,
            lineItems: allLineItems,
            ...totals,
            notes: notes || undefined,
        };
        try {
            const result = await saveMutation({
                variables: { input },
            });
            if (result.data?.saveTuitionCalculation?.success) {
                // Refetch to get the latest data
                await refetch();
                // Reset initialization flag to trigger state update from refetched data
                setIsInitialized(false);
                // Reset unsaved changes flag
                setHasUnsavedChanges(false);
                // Success feedback
                showAlert(t('Calculation saved successfully'), 'success');
            }
            else {
                // Error feedback
                const messages = result.data?.saveTuitionCalculation?.messages || [];
                showAlert(messages.map((m) => m.value).join('\n') ||
                    t('Failed to save calculation'), 'error');
            }
        }
        catch (error) {
            showAlert(t('Failed to save calculation'), 'error');
        }
    }, [
        calculationData?.tuitionCalculation?.id,
        tuitionCosts,
        nonReimbursableFees,
        creditsTuition,
        scholarshipsAndGrants,
        roomAndBoard,
        creditsHousing,
        totals,
        notes,
        paymentRequest.id,
        paymentRequest.clientId,
        saveMutation,
        refetch,
        t,
        showAlert,
    ]);
    // Render a section with line items
    const renderLineItemSection = (title = '', items, setter, type, description) => (_jsxs("div", { className: styles.section, children: [title && _jsx(Typography, { variant: 'h5', children: title }), description && (_jsx(Typography, { variant: 'body2', className: styles.description, children: description })), items.map((item, index) => (_jsxs("div", { className: styles.lineItem, children: [_jsx("div", { className: styles.currencySymbol, children: currencyDisplay }), _jsx(TextInput, { type: 'text', inputMode: 'decimal', label: t('Amount'), hideLabel: true, value: item.amount || '', onChange: e => {
                            const inputValue = e.target.value;
                            // Only allow numbers and decimal point with max 2 decimal places
                            if (inputValue === '' || /^\d*\.?\d{0,2}$/.test(inputValue)) {
                                handleUpdateItem(setter, item.tempId, 'amount', inputValue || undefined);
                            }
                        }, className: styles.amountInput, ref: (el) => {
                            if (el) {
                                inputRefs.current.set(item.tempId, el);
                            }
                            else {
                                inputRefs.current.delete(item.tempId);
                            }
                        } }), items.length > 1 && (_jsx(IconButton, { appearance: 'minimal', icon: _jsx(DeleteOutlinedIcon, { size: 20 }), label: t('Remove'), onClick: () => handleRemoveItem(setter, item.tempId) })), index === items.length - 1 && (_jsx(IconButton, { appearance: 'minimal', icon: _jsx(AddOutlinedIcon, { size: 20 }), label: t('Add another'), onClick: () => handleAddItem(setter, type), className: styles.addButton }))] }, item.tempId)))] }));
    // Render a calculated total row
    const renderTotal = (label, amount, description) => (_jsxs("div", { children: [_jsxs("div", { className: styles.totalRow, children: [_jsx(Typography, { variant: 'label1', children: label }), _jsxs(Typography, { variant: 'body1', children: [currencyDisplay, " ", formatDecimal(amount, 2)] })] }), description && (_jsx("div", { className: styles.formulaRow, children: _jsx(Typography, { variant: 'body2', className: styles.description, children: description }) }))] }));
    // Render unsaved changes indicator
    const renderUnsavedChanges = () => {
        if (!hasUnsavedChanges) {
            // return an empty div so the layout doesn't shift
            return _jsx("div", {});
        }
        return (_jsx(Typography, { variant: 'body2', className: styles.unsavedChangesText, children: t('Unsaved changes') }));
    };
    // Render last updated information
    const renderLastUpdated = () => {
        if (!savedCalculation?.updatedBy || !savedCalculation?.updatedAt) {
            return null;
        }
        const userName = `${savedCalculation.updatedBy.firstName} ${savedCalculation.updatedBy.lastName}`;
        const formattedDate = formatToLocalDatetime(savedCalculation.updatedAt, { dateStyle: 'short', timeStyle: 'short' }, '-', true);
        return (_jsx(Typography, { variant: 'body2', className: styles.lastUpdated, children: _jsx(FormattedMessage, { id: 'Last saved by {name} on {date}', values: {
                    name: _jsx("strong", { children: userName }),
                    date: formattedDate,
                } }) }));
    };
    if (loading) {
        return _jsx("div", { children: t('Loading...') });
    }
    // Get the saved calculation for metadata display
    const savedCalculation = calculationData?.tuitionCalculation;
    return (_jsxs("div", { className: styles.container, children: [(hasUnsavedChanges || savedCalculation?.updatedBy) && (_jsxs("div", { className: styles.headerRow, children: [renderUnsavedChanges(), renderLastUpdated()] })), renderLineItemSection(t('Tuition & mandatory fees'), tuitionCosts, setTuitionCosts, TuitionCalculationLineItemType.TUITION_COST, t("Cost of the student's tuition and mandatory fees")), renderTotal(t('Subtotal:'), totals.totalTuitionAndFees), renderLineItemSection(t('Non-reimbursable fees'), nonReimbursableFees, setNonReimbursableFees, TuitionCalculationLineItemType.NON_REIMBURSABLE_FEE, t('Voluntary meals, payment plans, convenience fees, etc.')), renderLineItemSection(t('Credits for tuition & mandatory fees'), creditsTuition, setCreditsTuition, TuitionCalculationLineItemType.CREDIT_TUITION, t("Any credits refunded to the student related to tuition and mandatory fees. If it's unknown what the refund is for, add it here.")), renderTotal(t('Subtotal:'), totals.subtotalAfterCredits, t('(tuition & mandatory fees) - credits for tuition & mandatory fees')), renderLineItemSection(t('Scholarships & Grants'), scholarshipsAndGrants, setScholarshipsAndGrants, TuitionCalculationLineItemType.SCHOLARSHIP_GRANT, t('Academic, athletic, social, grants, etc.')), renderTotal(t('Subtotal:'), totals.totalGrantsScholarships, t('scholarships & grants')), renderLineItemSection(t('Room and board'), roomAndBoard, setRoomAndBoard, TuitionCalculationLineItemType.ROOM_AND_BOARD, t('Non-reimbursable fee')), renderTotal(t('Subtotal:'), totals.totalRoomAndBoard, t('total room & board')), renderLineItemSection(t('Credits for housing & meals'), creditsHousing, setCreditsHousing, TuitionCalculationLineItemType.CREDIT_HOUSING, t('Any credits refunded to the student related to housing and meal credits.')), renderTotal(t('Subtotal:'), totals.roomAndBoardAfterCredits, t('room & board - credits for housing & meals')), _jsxs("div", { children: [_jsxs("div", { className: styles.finalTotal, children: [_jsx(Typography, { variant: 'h4', children: t('Reimbursable tuition') }), _jsxs(Typography, { variant: 'h4', children: [currencyDisplay, " ", formatDecimal(totals.reimbursableTuition, 2)] })] }), _jsx("div", { className: styles.formulaRow, children: _jsx(Typography, { variant: 'body2', className: styles.description, children: t('(tuition + mandatory - (credits for tuition & mandatory fees)) - (scholarships & grants - (room & board - credits for housing & meals))') }) })] }), _jsxs("div", { className: styles.notes, children: [_jsx(Typography, { variant: 'label1', children: t('Notes') }), _jsx("textarea", { className: styles.notesTextarea, value: notes, onChange: e => {
                            setNotes(e.target.value);
                            if (isInitialized)
                                setHasUnsavedChanges(true);
                        }, rows: 2, placeholder: t('Add notes...') })] }), _jsxs("div", { className: styles.actions, children: [renderUnsavedChanges(), _jsx(Button, { variant: 'contained', onClick: handleSave, disabled: saving, children: saving ? t('Saving...') : t('Save') })] })] }));
};
